import React from 'react';
import {
  FormLabel,
  FormControlLabel,
  Button,
  Radio,
  RadioGroup,
  Alert,
  Typography,
  CircularProgress,
  Paper,
  FormControl,
  TextField,
} from '@mui/material';
import PropTypes from 'prop-types';
import FormButtonGroup from '../components/layout/FormButtonGroup.jsx';
import { Box } from '@mui/system';
import { Divider } from '@mui/material';
import StudentRepository from '../services/StudentRepository.js';

export default function CivilStatusForm({
  repo,
  handleScreenNext,
  handleScreenBack,
  handleErrors,
  formValues,
  setFormValues,
  onSkip,
}) {
  const [loading, setLoading] = React.useState(false);
  const [errorOmission, setErrorOmission] = React.useState(false);
  const handleNext = async () => {
    const hasOmittedRequiredField = mayHaveChangedName() && !formValues.married_name;
    setErrorOmission(hasOmittedRequiredField);
    if (hasOmittedRequiredField) return;
    setLoading(true);
    repo
      .changeCivilStatus(formValues.civil_status, formValues.married_name)
      .then(() => {
        repo.changeFirstGenStatus(formValues.is_first_gen_graduate);
      })
      .then(() => {
        setLoading(false);
        handleScreenNext();
      })
      .catch((e) => {
        console.error(e);
        setLoading(false);
        handleErrors(
          <Alert variant="filled" severity="error" sx={{ marginBottom: 3 }}>
            <Typography variant="caption">
              We are unable to reflect the changes in your civil status. Please contact the
              administrator at webteam@msuiit.edu.ph.
            </Typography>
          </Alert>
        );
      });
  };
  const handleValueChange = (evt) => {
    if (errorOmission) setErrorOmission(false);
    const name = evt.target.name;
    const value = evt.target.value;
    const civilStatusesWithChangedName = ['married', 'separated', 'widowed'];
    let values = {
      [name]: value,
    };
    if (name == 'civil_status') {
      if (!civilStatusesWithChangedName.includes(value)) values = { ...values, married_name: null };
    }
    setFormValues({
      ...formValues,
      ...values,
    });
  };
  const mayHaveChangedName = () => {
    const civilStatusesWithChangedName = ['married', 'separated', 'widowed'];
    const result =
      civilStatusesWithChangedName.includes(formValues.civil_status) && formValues.gender !== 'M';
    return result;
  };
  return (
    <React.Fragment>
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          padding: '20px',
        }}
      >
        <FormControl>
          <FormLabel id="civil-stat-radio-group-label">Civil Status</FormLabel>
          <RadioGroup
            aria-labelledby="civil-stat-radio-group-label"
            name="civil_status"
            onChange={handleValueChange}
            value={formValues.civil_status}
          >
            <FormControlLabel value="single" control={<Radio />} label="Single" />
            <FormControlLabel value="married" control={<Radio />} label="Married" />
            <FormControlLabel value="separated" control={<Radio />} label="Separated" />
            <FormControlLabel
              value="single_parent"
              control={<Radio />}
              label="Single Parent (borne a child but not married)"
            />
            <FormControlLabel value="widowed" control={<Radio />} label="Widowed" />
          </RadioGroup>
          {mayHaveChangedName() && (
            <TextField
              required
              id="married_name"
              name="married_name"
              label="Name after marriage"
              variant="outlined"
              error={errorOmission}
              helperText={errorOmission ? 'Please fill out this field' : ''}
              onChange={handleValueChange}
              disabled={loading}
              value={formValues.married_name ? formValues.married_name : ''}
              fullWidth
              sx={{
                marginTop: '10px',
              }}
            />
          )}
        </FormControl>
        <div className={formValues.is_first_gen_graduate == null ? '' : 'hidden'}>
          <Divider light />
          <FormControl disabled={loading}>
            <FormLabel id="firstgen-radio-group-label">
              Were you the first college graduate in your entire family (including parents)?
            </FormLabel>
            <RadioGroup
              aria-labelledby="firstgen-radio-group-label"
              name="is_first_gen_graduate"
              onChange={handleValueChange}
              value={
                formValues.is_first_gen_graduate === null
                  ? ''
                  : formValues.is_first_gen_graduate === true ||
                      formValues.is_first_gen_graduate === '1'
                    ? '1'
                    : '0'
              }
            >
              <FormControlLabel value="1" control={<Radio />} label="Yes" />
              <FormControlLabel value="0" control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>
        </div>
      </Paper>
      <Box sx={{ marginTop: '20px' }}>
        <FormButtonGroup>
          <Button variant="contained" size="large" color="error" onClick={() => handleScreenBack()}>
            Back
          </Button>
          {onSkip && (
            <Button
              variant="outlined"
              size="large"
              color="primary"
              onClick={onSkip}
              sx={{ marginRight: '10px' }}
            >
              Skip to Finish
            </Button>
          )}
          <Box sx={{ m: 0, position: 'relative' }}>
            <Button
              variant="contained"
              size="large"
              color="primary"
              disabled={
                formValues.civil_status == null ||
                formValues.is_first_gen_graduate == null ||
                loading
              }
              onClick={handleNext}
            >
              Next
            </Button>
            {loading && (
              <CircularProgress
                size={24}
                color={'success'}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )}
          </Box>
        </FormButtonGroup>
      </Box>
    </React.Fragment>
  );
}

CivilStatusForm.propTypes = {
  repo: PropTypes.instanceOf(StudentRepository),
  studentId: PropTypes.string,
  handleScreenNext: PropTypes.func,
  handleScreenBack: PropTypes.func,
  handleFormValueChange: PropTypes.func,
  handleErrors: PropTypes.func,
  formValues: PropTypes.object,
  setFormValues: PropTypes.func,
  onSkip: PropTypes.func,
};
